import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { Tenant } from "@/mocks/superadmin/tenants";
import { adminApi, type AdminTenant } from "@/lib/superadmin";
import {
  TenantDetailBody,
  planLabels,
  statusConfig,
} from "../components/TenantDetailModal";
import ImpersonateModal from "../components/ImpersonateModal";
import { Card, ErrorBanner, Skeleton, GhostButton } from "../../components/ui";

/** Map a real API tenant onto the shape the UI components render. */
function toTenant(t: AdminTenant): Tenant {
  return {
    id: t.id,
    businessName: t.businessName,
    slug: t.slug,
    ownerName: t.ownerName,
    email: t.email,
    phone: t.phone ?? "—",
    city: t.city,
    address: t.address ?? "—",
    gstin: t.gstin ?? "—",
    fssai: t.fssai ?? "—",
    plan: t.plan,
    status: t.status,
    outlets: t.outlets,
    signupDate: t.signupDate,
    lastActive: t.lastActive ?? "—",
    monthlyRevenue: t.monthlyRevenue,
    ordersPerDay: t.ordersPerDay,
  };
}

/**
 * Full-page tenant detail at /superadmin/tenants/:id.
 * Prefers the tenant passed via router state (from the list click); falls back
 * to fetching the list and finding by id so deep links / refreshes work.
 */
export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const stateTenant = (location.state as { tenant?: Tenant } | null)?.tenant;

  const [tenant, setTenant] = useState<Tenant | null>(
    stateTenant && stateTenant.id === id ? stateTenant : null,
  );
  const [loading, setLoading] = useState(!tenant);
  const [error, setError] = useState("");
  const [impersonateTenant, setImpersonateTenant] = useState<Tenant | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [canImpersonate, setCanImpersonate] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.tenants.list({ limit: 500 });
      const row = res.rows.find((r) => r.id === id);
      if (!row) {
        setError("Tenant not found.");
        setTenant(null);
      } else {
        setTenant(toTenant(row));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load tenant.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Always refresh from the API (also picks up edits made on this page).
  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirmImpersonate = async () => {
    if (!impersonateTenant) return;
    setImpersonating(true);
    try {
      const { url } = await adminApi.tenants.impersonateLink(impersonateTenant.id);
      window.open(url, "_blank", "noopener");
      showToast(`Opened ${impersonateTenant.businessName} session.`);
      setImpersonateTenant(null);
    } catch (e) {
      setCanImpersonate(false);
      showToast(e instanceof Error ? e.message : "Impersonation unavailable.");
      setImpersonateTenant(null);
    } finally {
      setImpersonating(false);
    }
  };

  const stColor =
    tenant?.status === "active" ? "bg-emerald-500"
    : tenant?.status === "trial" ? "bg-amber-400"
    : tenant?.status === "churned" ? "bg-red-400" : "bg-slate-400";

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[980px] mx-auto">
      {toast && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2 bg-foreground-900 text-background-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium">
          <i className="ri-check-line text-emerald-400"></i>
          {toast}
        </div>
      )}

      {/* Breadcrumb / back */}
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/superadmin/tenants"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          All tenants
        </Link>
        <GhostButton onClick={() => void load()}>
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
          Refresh
        </GhostButton>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading && !tenant ? (
        <Card className="space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-xl" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-5 w-52" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </Card>
      ) : tenant ? (
        <>
          {/* Header card */}
          <Card className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-store-2-line text-xl text-primary-600"></i>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg md:text-xl font-heading font-bold text-foreground-900 truncate">
                    {tenant.businessName}
                  </h1>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full border shrink-0 ${
                      statusConfig[tenant.status].classes
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${stColor}`}></span>
                    {statusConfig[tenant.status].label}
                  </span>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 shrink-0">
                    {tenant.plan ? (planLabels[tenant.plan] ?? tenant.plan) : "No plan"}
                  </span>
                </div>
                <p className="text-xs text-foreground-400 truncate mt-0.5">
                  {tenant.slug ? `/${tenant.slug}` : tenant.id} · {tenant.ownerName} · {tenant.city}
                </p>
              </div>
            </div>
            {canImpersonate && (
              <button
                onClick={() => setImpersonateTenant(tenant)}
                className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shield-user-line"></i>
                Login As {tenant.ownerName.split(" ")[0]}
              </button>
            )}
          </Card>

          {/* Full detail body (business details, usage, subscription, team, overrides) */}
          <Card>
            <TenantDetailBody
              tenant={tenant}
              onImpersonate={canImpersonate ? setImpersonateTenant : undefined}
              onChanged={() => void load()}
            />
          </Card>
        </>
      ) : !error ? (
        <Card padded={false}>
          <div className="py-12 text-center">
            <p className="text-sm text-foreground-400">Tenant not found.</p>
            <button
              onClick={() => navigate("/superadmin/tenants")}
              className="mt-3 text-sm font-semibold text-primary-600 hover:underline cursor-pointer"
            >
              Back to tenants
            </button>
          </div>
        </Card>
      ) : null}

      {impersonateTenant && (
        <ImpersonateModal
          tenant={impersonateTenant}
          onClose={() => setImpersonateTenant(null)}
          onConfirm={handleConfirmImpersonate}
          loading={impersonating}
        />
      )}
    </div>
  );
}
